import { useCallback, useEffect, useState } from 'react';
import { followPlannerEvents, phoneApi, SyncRequestError, type PairedPhone } from './phone-sync.js';

export interface PhonesState {
  /** Paired phones; undefined until first loaded. */
  phones?: PairedPhone[];
  /** The event stream is open, so the state is current. */
  live: boolean;
  /** Why the phones could not be loaded. */
  error?: string;
}

/**
 * The paired phones and what they have, kept current for the whole page
 * through the planner's event stream (#35): `devices` when a phone pairs,
 * unpairs, connects, downloads or stores a trip, `trips` when a trip is
 * written or planned. Phones and trips reload on every (re)connection.
 */
export function usePhones(plannerKey: string, onTrips: () => void): PhonesState & { refresh: () => Promise<PairedPhone[] | undefined>; reconnect: () => void } {
  const [api] = useState(() => phoneApi(plannerKey));
  const [state, setState] = useState<PhonesState>({ live: false });
  const [connection, setConnection] = useState(0);

  const loadPhones = useCallback(async () => {
    try {
      const phones = await api.devices();
      setState((current) => ({ ...current, phones, error: undefined }));
      return phones;
    } catch (error) {
      // Keep the last known list; a network failure means the planner API is down or restarting.
      setState((current) => ({ ...current, error: error instanceof SyncRequestError ? error.message : 'The planner API cannot be reached. Reconnecting…' }));
      return undefined;
    }
  }, [api]);

  useEffect(() => {
    const stop = new AbortController();
    void loadPhones();
    void followPlannerEvents(plannerKey, (event) => {
      if (event === 'closed') { setState((current) => ({ ...current, live: false })); return; }
      if (event === 'open') setState((current) => ({ ...current, live: true }));
      if (event === 'open' || event === 'devices') void loadPhones();
      if (event === 'open' || event === 'trips') onTrips();
    }, stop.signal);
    return () => stop.abort();
  }, [plannerKey, loadPhones, onTrips, connection]);

  return { ...state, refresh: loadPhones, reconnect: () => setConnection((value) => value + 1) };
}
